import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import { useAppDispatch, useAppSelector } from 'src/store';
import MapResetButton from 'src/shared/components/map-reset-button/MapResetButton';

import useCustomer from 'src/app/customers/hooks/useCustomer';
import ReactLeafletCustomControl from 'src/shared/components/react-leaflet-custom-control/ReactLeafletCustomControl';
import MapType from 'src/shared/components/map-type/MapType';
import config from 'src/config';
import { isValidCoordinates } from 'src/shared/helpers/coordinateHelpers';
import 'src/shared/styles/leaflet.css';
import googleMapStyles from 'src/shared/data/googleMapStyles';
import { getCurrentPortfolioFilters } from 'src/app/portfolio/state/portfolioGeneralSelector';
import { updateUiState } from 'src/app/portfolio/views/portfolio-site-explorer/state/portfolioSiteExplorerSlice';
import { getPortfolioSiteExplorerState } from 'src/app/portfolio/views/portfolio-site-explorer/state/portfolioSiteExplorerSelector';
import styles from './PortfolioSiteExplorerMapMain.module.scss';
import usePortfolioSiteExplorerAnalytics from '../../hooks/usePortfolioSiteExplorerAnalytics';
import PortfolioSiteExplorerPolygon from '../map-polygon/PortfolioSiteExplorerMapPolygon';
import PortfolioSiteExplorerMarkers from '../map-markers/PortfolioSiteExplorerMapMarkers';
import PortfolioSiteExplorerLegend from '../map-legend/PortfolioSiteExplorerMapLegend';
import { useMapMarkersQuery } from '../../state/api/portfolioSiteExplorerGraphSlice';

const PortfolioSiteExplorerMain = () => {
    const dispatch = useAppDispatch();
    const selectedFilters = useAppSelector(getCurrentPortfolioFilters);
    const uiState = useAppSelector(getPortfolioSiteExplorerState);
    const { mapType, selectedMetric } = uiState;
    const mapRef = useRef<L.Map>(null);
    const { currentCustomerId } = useCustomer();
    const { trackMapTypeChange, trackMapZoom, trackMapDrag } = usePortfolioSiteExplorerAnalytics();

    // Auto-show polygon when zooming in and only one marker is visible
    const [autoShowPolygon, setAutoShowPolygon] = useState<string | null>(null);

    const isSpeciesMetric = selectedMetric === 'POSSIBLE_IUCN_RED_LIST_SPECIES' || selectedMetric === 'POSSIBLE_INVASIVE_SPECIES';

    const { currentData: markersData, isFetching: isMarkersFetching } = useMapMarkersQuery(
        {
            customerId: currentCustomerId,
            ...(isSpeciesMetric ? {} : { metric: selectedMetric }),
            ...(selectedFilters.length ? { filters: selectedFilters } : {}),
        },
        { skip: selectedMetric === 'NA' }
    );

    const markers = markersData?.markers ?? [];

    // Valid coordinates for bounds/autoshow calculations
    const validMarkers = React.useMemo(() => markers.filter(m => isValidCoordinates(m.coordinates as [number, number])), [markers]);

    const getBounds = () => {
        if (validMarkers.length > 0) {
            const bounds = L.latLngBounds(validMarkers.map(m => m.coordinates as [number, number]));
            if (bounds.isValid()) return bounds;
        }
        return undefined;
    };

    const getDefaultBounds = () => {
        if (validMarkers.length === 0) {
            return L.latLngBounds([
                [-85, -180],
                [85, 180],
            ]);
        }
        if (validMarkers.length === 1) {
            const [lat, lng] = validMarkers[0].coordinates;
            const padding = 0.1;
            return L.latLngBounds([lat - padding, lng - padding], [lat + padding, lng + padding]);
        }
        const bounds = L.latLngBounds(validMarkers.map(m => m.coordinates as [number, number]));
        bounds.pad(0.05);
        return bounds;
    };

    // Domain for species metrics — derived directly from markers
    const speciesColorDomain: { min: number; max: number } | undefined = React.useMemo(() => {
        if (!isSpeciesMetric) return undefined;
        const values = markers.map(m =>
            selectedMetric === 'POSSIBLE_IUCN_RED_LIST_SPECIES' ? m.speciesRedListCount ?? 0 : m.speciesInvasiveCount ?? 0
        );
        if (values.length === 0) return undefined;
        const max = Math.max(...values);
        if (!(max > 0)) return undefined;
        return { min: Math.min(...values), max };
    }, [markers, selectedMetric, isSpeciesMetric]);

    const selectedMarker = markers.find(m => m.siteId === uiState.selectedSiteId);

    // Ensure selected site marker is visible in the viewport, positioned 1/3 from bottom for tooltip space
    useEffect(() => {
        if (!mapRef.current || !selectedMarker) {
            return;
        }
        const map = mapRef.current;
        const coordinates = selectedMarker.coordinates as [number, number];
        if (isValidCoordinates(coordinates)) {
            let currentZoom = map.getZoom();
            if (currentZoom < 4) {
                currentZoom = 4;
            }

            // Get the current viewport bounds
            const bounds = map.getBounds();

            // Calculate offset to position marker in lower portion of viewport
            // This gives enough space above the marker for tooltip display
            const latOffset = (bounds.getNorth() - bounds.getSouth()) * 0.3;
            const adjustedLat = coordinates[0] + latOffset;

            // Create new coordinates with adjusted latitude
            const adjustedCoordinates = L.latLng(adjustedLat, coordinates[1]);

            // Fly to adjusted coordinates
            map.flyTo(adjustedCoordinates, currentZoom, {
                animate: true,
                duration: 0.5,
                easeLinearity: 0.25,
            });
        }
    }, [selectedMarker]);

    useEffect(() => {
        if (!mapRef.current) {
            return;
        }

        const map = mapRef.current;

        const handleMapMove = () => {
            const currentBounds = map.getBounds();
            const visibleMarkers = validMarkers.filter(m => currentBounds.contains(m.coordinates as [number, number]));

            // If only one site is visible and we're zoomed in enough, show its polygon
            if (visibleMarkers.length === 1 && map.getZoom() >= 10) {
                setAutoShowPolygon(visibleMarkers[0].siteId);
            } else {
                setAutoShowPolygon(null);
            }
        };

        const handleMapZoom = () => {
            const zoomLevel = map.getZoom();
            trackMapZoom(zoomLevel);
            handleMapMove();
        };

        const handleMapDrag = () => {
            trackMapDrag();
            handleMapMove();
        };

        map.on('moveend', handleMapDrag);
        map.on('zoomend', handleMapZoom);

        return () => {
            map.off('moveend', handleMapDrag);
            map.off('zoomend', handleMapZoom);
        };
    }, [validMarkers]);

    // Hook responsible to fit the bounds once the data is loaded
    useEffect(() => {
        const bounds = getBounds();
        if (bounds && mapRef.current) {
            mapRef.current.fitBounds(bounds, {
                duration: 1.5,
                easeLinearity: 0.25,
                padding: [20, 20],
            });
        }
    }, [validMarkers]);

    const resetMapView = () => {
        if (mapRef.current && validMarkers.length > 0) {
            const bounds = L.latLngBounds(validMarkers.map(m => m.coordinates as [number, number]));
            if (bounds.isValid()) {
                mapRef.current.flyToBounds(bounds, {
                    duration: 1.5,
                    easeLinearity: 0.25,
                    padding: [20, 20],
                });
            }
        }
    };

    const handleMapTypeChange = (value: string) => {
        trackMapTypeChange(value);
        dispatch(
            updateUiState({
                mapType: value,
            })
        );
    };

    if (isMarkersFetching) {
        return (
            <div className={styles.mapContainer}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>Loading map data...</div>
            </div>
        );
    }

    if (!markersData) {
        return (
            <div className={styles.mapContainer}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    No portfolio data available
                </div>
            </div>
        );
    }

    return (
        <div className={styles.mapContainer}>
            <MapContainer
                ref={mapRef}
                bounds={getDefaultBounds()}
                zoomControl={false}
                className={styles.leafletMap}
                maxBoundsViscosity={1.0}
            >
                <ReactLeafletGoogleLayer
                    key={mapType}
                    useGoogMapsLoader={true}
                    apiKey={config.googleMapsJavascriptApiKey}
                    {...({ type: mapType } as any)}
                    {...({ styles: googleMapStyles } as any)}
                />

                <PortfolioSiteExplorerMarkers selectedMetric={selectedMetric} speciesColorDomain={speciesColorDomain} />

                <PortfolioSiteExplorerPolygon
                    siteId={uiState.selectedSiteId || autoShowPolygon || undefined}
                    selectedMetric={selectedMetric}
                    speciesColorDomain={speciesColorDomain}
                />

                <ZoomControl position='topright' />

                {/* Reset view button */}
                <ReactLeafletCustomControl position='topright'>
                    <MapResetButton onClick={resetMapView} />
                </ReactLeafletCustomControl>

                {/* Map Legend */}
                <div className='leaflet-bottom leaflet-left ml-4'>
                    <PortfolioSiteExplorerLegend selectedMetric={selectedMetric} />
                </div>

                {/* Map Type Selector */}
                <div
                    className='leaflet-bottom leaflet-right'
                    style={{ pointerEvents: 'visible', right: '15px' }}
                    ref={ref => {
                        if (!ref) {
                            return;
                        }
                        L.DomEvent.disableClickPropagation(ref).disableScrollPropagation(ref);
                    }}
                >
                    <div className={styles.legend}>
                        <div className='text-lg text-grey-80 mb-2'>Map Type </div>
                        <MapType handleChange={handleMapTypeChange} value={mapType} />
                    </div>
                </div>
            </MapContainer>
        </div>
    );
};

export default PortfolioSiteExplorerMain;
